
import { computed, defineComponent, unref } from '@nuxtjs/composition-api'

export default defineComponent({
  props: {
    count: { type: Number, required: true, validator: (page) => page >= 0 },
    limit: { type: Number, required: true, validator: (page) => page > 0 },
    page: { type: Number, required: true, validator: (page) => page > 0 },
    showPrevious: { type: Boolean, required: false, default: true },
    showNext: { type: Boolean, required: false, default: true },
  },
  emits: ['click'],
  setup(props, { emit }) {
    const highestPage = computed(() => Math.ceil(props.count / props.limit))
    const currentPage = computed(() => Math.min(unref(highestPage), props.page))
    const enablePrevious = computed(() => props.showPrevious && unref(currentPage) >= 2)
    const enableNext = computed(() => props.showNext && unref(currentPage) < unref(highestPage))
    const links = computed(() => {
      const _currentPage = unref(currentPage)
      const _highestPage = unref(highestPage)
      if (_highestPage < 6) {
        // only the first 5 links are needed
        return [...Array(_highestPage).keys()].map((offset) => ({ type: 'link', page: offset + 1 }))
      } else if (_currentPage > 3 && _currentPage < _highestPage - 2) {
        // replace links with dots at the beginning and end
        return [{ type: 'dots' }, ...[-1, 0, 1].map((offset) => ({ type: 'link', page: _currentPage + offset })), { type: 'dots' }]
      } else if (_currentPage <= _highestPage / 2) {
        // replace links with dots at the end
        return [...[1, 2, 3, 4].map((offset) => ({ type: 'link', page: offset })), { type: 'dots' }]
      } else {
        // replace links with dots at the beginning
        return [{ type: 'dots' }, ...[3, 2, 1, 0].map((offset) => ({ type: 'link', page: _highestPage - offset }))]
      }
    })

    // Emit a 'click' event to switch to a different page
    const onClick = (targetPage) => {
      if (targetPage && targetPage !== unref(currentPage)) {
        emit('click', { page: Math.max(1, Math.min(unref(highestPage), unref(targetPage))) })
        window.scrollTo(0, 0)
      }
    }

    return { enableNext, enablePrevious, links, onClick, currentPage, highestPage }
  },
})
